import { useEffect, useState } from 'react'
import { Search, ExternalLink, Eye } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Q {
  category: string
  title: string
  query: string
  risk: 'critical' | 'high' | 'medium' | 'low'
  description: string
  shodan_url: string
}

const RISK_TONE: Record<string, string> = {
  critical: 'bg-rose/15 text-rose border-rose/30',
  high:     'bg-amber/15 text-amber-light border-amber/30',
  medium:   'bg-blue/15 text-blue-light border-blue/30',
  low:      'bg-emerald/15 text-emerald border-emerald/30',
}

export default function ShodanQueriesPage() {
  const [queries, setQueries] = useState<Q[]>([])
  const [cats, setCats] = useState<Record<string, number>>({})
  const [cat, setCat] = useState<string>('all')
  const [q, setQ] = useState('')

  useEffect(() => {
    api.get('/api/sec/shodan-queries').then(r => {
      setQueries(r.data.queries || [])
      setCats(r.data.categories || {})
    }).catch(() => {})
  }, [])

  const filtered = queries
    .filter(x => cat === 'all' || x.category === cat)
    .filter(x => !q || x.title.toLowerCase().includes(q.toLowerCase())
              || x.description.toLowerCase().includes(q.toLowerCase())
              || x.query.toLowerCase().includes(q.toLowerCase()))

  return (
    <div className="space-y-4 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
          <Eye size={24} className="text-cyan" /> Shodan Saved-Query Board
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Curated dorks for finding exposed services. Click <em>open in Shodan</em> to run with your own account.
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => setCat('all')}
          className={clsx('badge text-[10px] px-2 py-1', cat === 'all' ? 'badge-blue' : 'bg-surface-card text-text-muted border border-border')}>
          All ({queries.length})
        </button>
        {Object.entries(cats).map(([c, n]) => (
          <button key={c} type="button" onClick={() => setCat(c === cat ? 'all' : c)}
            className={clsx('badge text-[10px] px-2 py-1',
              cat === c ? 'badge-blue' : 'bg-surface-card text-text-muted border border-border')}>
            {c} <span className="text-text-muted ml-1">{n}</span>
          </button>
        ))}
        <div className="relative flex-1 max-w-xs ml-auto">
          <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder="filter…" className="input pl-7 py-1.5 text-[11px] w-full" />
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((qq, i) => (
          <div key={i} className={clsx('card p-3 border', RISK_TONE[qq.risk])}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[9px] uppercase tracking-wider font-bold">{qq.risk}</span>
              <span className="text-[9px] text-text-muted">{qq.category}</span>
            </div>
            <h3 className="text-xs font-semibold text-text-primary mb-1">{qq.title}</h3>
            <p className="text-[10px] text-text-secondary mb-2">{qq.description}</p>
            <code className="block text-[10px] font-mono bg-surface-raised p-1.5 rounded mb-2 text-text-secondary truncate" title={qq.query}>
              {qq.query}
            </code>
            <a href={qq.shodan_url} target="_blank" rel="noreferrer"
              className="text-[10px] text-blue-light hover:underline inline-flex items-center gap-1">
              Open in Shodan <ExternalLink size={9} />
            </a>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <p className="card p-6 text-center text-text-muted text-[11px] italic">No matching queries.</p>
      )}
    </div>
  )
}
